import { useEffect, useState } from "react";
import { Plus, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { ACCENT_KEYS, accentFor } from "../../lib/accents";
import { Switch } from "../../components/ui/switch";
import { useConfirm } from "../../components/ConfirmDialog";

const CATEGORY_CAP = 10;

export default function AdminCategories() {
  const confirm = useConfirm();
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [color, setColor] = useState("aqua");

  const load = () => api.get("/admin/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const atCap = cats.length >= CATEGORY_CAP;

  const create = async (e) => {
    e.preventDefault();
    if (atCap) {
      toast.error(`You've hit the ${CATEGORY_CAP}-category cap. Delete an unused one to make room.`);
      return;
    }
    try {
      await api.post("/admin/categories", { name, color });
      setName("");
      toast.success("Category created");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/categories/${c.id}`, { active: !c.active });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (c) => {
    if (!(await confirm({
      title: `Delete "${c.name}"?`,
      message: "It will be removed from every ad that uses it. The ads themselves are kept.",
      tone: "danger",
      confirmText: "Delete category",
    }))) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Category deleted");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="fade-up max-w-2xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-3xl text-[#000000]">Categories</h1>
          <p className="text-gray-500 mt-1 text-sm">Organize the library. Colors power the chips clients see.</p>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${atCap ? "bg-[#DF1283] text-white" : cats.length >= CATEGORY_CAP * 0.8 ? "bg-[#FC861E] text-white" : "bg-[#D0FF0D] text-black"}`} data-testid="category-cap-indicator">
          {cats.length} / {CATEGORY_CAP} categories
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-xs text-gray-500 bg-[#0FFFE2] border border-[#0FFFE2] rounded-xl p-3">
        <Info size={14} className="text-[#000000] mt-0.5 shrink-0" />
        <div>
          Keep your taxonomy tight. Every ad falls into one of four buckets — <strong className="text-[#000000]">Product, Service, Event, or Experience</strong> — plus a few marketing archetypes (Personal Brand, Social Proof, Founder Story, etc.). The AI auto-tagger is locked to this list and won&rsquo;t invent new ones. Delete any you don&rsquo;t need to make room.
        </div>
      </div>

      <form onSubmit={create} className="mt-6 bg-white border border-gray-200 rounded-xl p-5 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Name</label>
          <input data-testid="category-name-input" required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Product Demo"
            disabled={atCap}
            className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2] disabled:bg-gray-50 disabled:cursor-not-allowed" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Color</label>
          <div className="flex gap-2 mt-2.5">
            {ACCENT_KEYS.map((k) => (
              <button key={k} type="button" data-testid={`color-swatch-${k}`} onClick={() => setColor(k)}
                className={`w-7 h-7 rounded-full transition-transform ${color === k ? "ring-2 ring-offset-2 ring-[#000000] scale-110" : ""}`}
                style={{ background: accentFor(k).hex }} aria-label={k} />
            ))}
          </div>
        </div>
        <button data-testid="create-category-btn" type="submit" disabled={atCap}
          className="flex items-center gap-2 bg-[#000000] text-white rounded-full px-5 py-2.5 font-bold text-sm hover:bg-[#4A0A66] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={15} /> {atCap ? "Cap reached" : "Add"}
        </button>
      </form>

      <div className="mt-6 space-y-2" data-testid="categories-list">
        {cats.map((c) => (
          <div key={c.id} className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-4" data-testid={`category-row-${c.slug}`}>
            <div className="w-4 h-4 rounded-full shrink-0" style={{ background: accentFor(c.color).hex }} />
            <div className="flex-1 min-w-0">
              <span className="font-display font-bold text-sm text-[#000000]">{c.name}</span>
              <span className="text-xs text-gray-400 ml-3">{c.ad_count} ad{c.ad_count !== 1 ? "s" : ""}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">{c.active ? "Active" : "Hidden"}</span>
              <Switch data-testid={`category-active-switch-${c.slug}`} checked={c.active} onCheckedChange={() => toggleActive(c)} />
            </div>
            <button data-testid={`delete-category-btn-${c.slug}`} onClick={() => remove(c)}
              className="p-2 rounded-full hover:bg-[#DF1283] text-gray-400 hover:text-white transition-colors"><Trash2 size={15} /></button>
          </div>
        ))}
        {cats.length === 0 && <div className="text-center py-10 text-gray-400 text-sm">No categories yet.</div>}
      </div>
    </div>
  );
}
